import React from 'react';
import { Phone, MessageSquare, Mic } from 'lucide-react';
import V2Layout from '../components/V2Layout';
import {
  FadeUp, SectionHead, CtaGhost, PageHero, Check, RuleLabel, TextLink,
  AgentAvatar, OSWindow, BigStat, ClosingCta,
} from '../components/V2Shared';
import { AGENTS_FRONT } from '../data/site';

/* per-agent vignette: a small slice of the agent actually working */
const VIGNETTES = {
  Amy: {
    title: 'amy / live call — 9:48 pm',
    icon: Phone,
    lines: [
      { who: 'Caller', text: '“Hi — my crown just came off and I\'m flying out Friday…”' },
      { who: 'Amy', text: '“I\'m sorry — let\'s get you in before your trip. Any pain or swelling right now?”' },
      { who: 'Amy', text: 'Checked Dr. Patel\'s Thursday block · verified PPO coverage live · booked 10:20a' },
      { who: 'System', text: 'Appointment created → confirmation sent → chart flagged for crown re-cement', mono: true },
    ],
  },
  Leena: {
    title: 'leena / gap fill — 9:41 am',
    icon: MessageSquare,
    lines: [
      { who: 'System', text: '11:00a cancellation detected · operatory 2 · 60 min', mono: true },
      { who: 'Leena', text: 'Matched 3 waitlist patients by procedure, insurance, and drive time' },
      { who: 'Leena', text: 'Texted two, called one — first yes wins the chair' },
      { who: 'System', text: 'Slot refilled in 12 minutes · prep instructions sent', mono: true },
    ],
  },
  Stephanie: {
    title: 'stephanie / recall call — 12:30 pm',
    icon: Phone,
    lines: [
      { who: 'Stephanie', text: '“Last time we spoke you were traveling — welcome back! Dr. Lee noted some early gum changes…”' },
      { who: 'Patient', text: '“Oh right, I kept meaning to call. Do you have any late afternoons?”' },
      { who: 'System', text: 'Booked 4:40p with preferred hygienist · 14 months since last visit', mono: true },
    ],
  },
  Kim: {
    title: 'kim / treatment follow-up — 3:15 pm',
    icon: MessageSquare,
    lines: [
      { who: 'System', text: 'Unscheduled plan: 2 crowns, 1 filling · presented 11 days ago', mono: true },
      { who: 'Kim', text: 'Sent plain-language explainer of why the cracked molar can\'t wait' },
      { who: 'Kim', text: 'Patient worried about cost → offered phasing: urgent crown first' },
      { who: 'System', text: 'Phase 1 scheduled · phase 2 follow-up queued for spring', mono: true },
    ],
  },
  Elise: {
    title: 'elise / post-visit — 5:32 pm',
    icon: Phone,
    lines: [
      { who: 'Elise', text: '“Just checking in after your visit today — how did everything feel?”' },
      { who: 'Patient', text: '“Honestly, best cleaning I\'ve had. The numbing took a while though.”' },
      { who: 'Elise', text: 'Logged feedback for the clinical team · sent one-tap review link' },
      { who: 'System', text: 'Five-star review posted 9 minutes later', mono: true },
    ],
  },
  Morgan: {
    title: 'morgan / intake prep — tuesday',
    icon: MessageSquare,
    lines: [
      { who: 'Morgan', text: 'Texted forms Monday · gentle nudge Tuesday morning · done by lunch' },
      { who: 'Patient', text: '[ photo of insurance card ]' },
      { who: 'System', text: 'Card parsed → member ID extracted → handed to Amanda for verification', mono: true },
      { who: 'Morgan', text: 'Flagged: pacemaker + warfarin → provider briefed before the chair' },
    ],
  },
  Trisha: {
    title: 'trisha / morning diagnostics — 6:00 am',
    icon: Mic,
    lines: [
      { who: 'Trisha', text: 'Health check across all agents: 11 green, 1 attention' },
      { who: 'System', text: 'Payer portal outage detected → verification queue backing up', mono: true },
      { who: 'Trisha', text: 'Rerouted 15 verifications to Laura\'s phone workflow' },
      { who: 'Trisha', text: 'Morning brief sent to the office manager — nothing needs you yet' },
    ],
  },
  Joseph: {
    title: 'joseph / fax intake — all day',
    icon: MessageSquare,
    lines: [
      { who: 'System', text: 'Inbound fax · 6 pages · sender: oral surgery group', mono: true },
      { who: 'Joseph', text: 'Classified: referral + radiograph → filed to the right patient' },
      { who: 'Joseph', text: 'Alerted the treatment coordinator with a one-line summary' },
      { who: 'System', text: '14 faxes today · 0 touched a human first', mono: true },
    ],
  },
};

const AgentBlock = ({ agent, index }) => {
  const vig = VIGNETTES[agent.name];
  const flip = index % 2 === 1;
  return (
    <div id={agent.name.toLowerCase()} className="scroll-mt-32">
      <RuleLabel label={`${agent.tag} / ${agent.name} — ${agent.role}`} />
      <div className={`mt-10 grid items-center gap-12 lg:grid-cols-2 lg:gap-20 ${flip ? 'lg:[&>*:first-child]:order-2' : ''}`}>
        <FadeUp>
          <div className="flex items-center gap-5">
            <AgentAvatar name={agent.name} img={agent.img} size="h-16 w-16" />
            <div>
              <h2 className="v2-display text-[32px] font-semibold leading-none text-[#0a1633] md:text-[40px]">{agent.name}</h2>
              <div className="v2-mono mt-2 text-[11.5px] font-medium uppercase tracking-[0.14em] text-[#1a46e5]">{agent.role}</div>
            </div>
          </div>
          <p className="mt-6 max-w-xl text-[16px] leading-relaxed text-[#3f4d6e]">{agent.detail}</p>
          <ul className="mt-6 space-y-2.5">
            {agent.points.map((pt) => (
              <Check key={pt}>{pt}</Check>
            ))}
          </ul>
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="v2-cross relative">
            <div className="v2-canvas rounded-2xl p-7 md:p-9">
              <OSWindow title={vig.title}>
                <div className="space-y-3 bg-white p-5">
                  {vig.lines.map((l, i) => (
                    <div key={i} className={`flex gap-3 ${l.mono ? '' : ''}`}>
                      <span className={`v2-mono w-16 shrink-0 pt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                        l.who === agent.name ? 'text-[#1a46e5]' : l.mono || l.who === 'System' ? 'text-[#0f9d76]' : 'text-[#7c89a8]'
                      }`}>
                        {l.who}
                      </span>
                      <p className={`text-[13.5px] leading-relaxed ${
                        l.mono || l.who === 'System' ? 'v2-mono text-[12px] text-[#7c89a8]' : 'text-[#3f4d6e]'
                      }`}>
                        {l.text}
                      </p>
                    </div>
                  ))}
                </div>
              </OSWindow>
            </div>
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

const V2FrontOffice = () => (
  <V2Layout title="Front Office AI — Asha">
    <PageHero
      eyebrow="The AI workforce / front office"
      title={<>A front office with <span className="text-[#1a46e5]">no hold music.</span></>}
      body="Eight AI employees covering the desk around the clock — every call answered, every gap filled, every overdue patient called back, every form done before the visit. Your team keeps the judgment calls; the agents keep the phones."
    >
      <CtaGhost to="/book-demo">Hear Amy take a call</CtaGhost>
      <TextLink to="/v2/revenue-cycle">Next: the revenue cycle team</TextLink>
    </PageHero>

    {/* roster strip */}
    <section className="px-6 pb-10">
      <div className="mx-auto max-w-7xl">
        <FadeUp>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-[#dde5f3] bg-[#dde5f3] sm:grid-cols-4 lg:grid-cols-8">
            {AGENTS_FRONT.map((a) => (
              <a key={a.name} href={`#${a.name.toLowerCase()}`} className="group flex flex-col items-center gap-2.5 bg-white px-3 py-6 text-center transition-colors hover:bg-[#f6f9ff]">
                <AgentAvatar name={a.name} img={a.img} size="h-12 w-12" />
                <div>
                  <div className="text-[13.5px] font-bold text-[#0a1633] group-hover:text-[#1a46e5]">{a.name}</div>
                  <div className="mt-0.5 text-[11px] leading-tight text-[#7c89a8]">{a.role}</div>
                </div>
              </a>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>

    {/* agent dossiers */}
    <section className="px-6 py-14">
      <div className="mx-auto max-w-7xl space-y-20 md:space-y-28">
        {AGENTS_FRONT.map((a, i) => (
          <AgentBlock key={a.name} agent={a} index={i} />
        ))}
      </div>
    </section>

    {/* coverage panel */}
    <section className="px-6 py-24 md:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="v2-panel relative overflow-hidden rounded-[2rem] px-7 py-16 md:px-14 md:py-24">
          <SectionHead
            dark
            center
            eyebrow="coverage"
            title={<>The desk is staffed before sunrise and after close.</>}
            body="Calls at 9 pm get answered. Cancellations at 7 am get filled. Recall happens every single day, not when someone finds an hour. That's what a workforce that never clocks out looks like."
          />
          <FadeUp delay={0.15}>
            <div className="mx-auto mt-14 grid max-w-4xl grid-cols-2 gap-y-10 border-t border-white/15 pt-10 md:grid-cols-4">
              <BigStat dark value="24/7" label="call coverage" />
              <BigStat dark value="8" label="agents on the desk" />
              <BigStat dark value="0" label="calls to voicemail" />
              <BigStat dark value="1st" label="call books the visit" />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>

    <ClosingCta
      title="Give your front desk a night off."
      body="Book a demo and listen to Amy handle a real after-hours emergency call, end to end."
    />
  </V2Layout>
);

export default V2FrontOffice;
